<template>
  <div class="admin-users-page">
    <div class="page-header">
      <h1 class="page-title">유저 관리</h1>
      <p class="page-subtitle">총 {{ users.length }}명</p>
    </div>

    <!-- 검색 -->
    <div class="search-bar">
      <input type="text" v-model="searchQuery" placeholder="이메일로 검색..." />
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
    </div>

    <div v-else class="user-list">
      <div v-for="user in filteredUsers" :key="user.id" class="user-item">
        <div class="user-avatar">
          {{ user.email?.charAt(0).toUpperCase() }}
        </div>
        <div class="user-info">
          <h3>{{ user.email }}</h3>
          <p>가입일: {{ formatDate(user.createdAt) }}</p>
        </div>
        <div class="user-role">
          <select v-model="user.role" @change="updateRole(user)">
            <option value="user">일반회원</option>
            <option value="admin">관리자</option>
          </select>
        </div>
        <div class="user-stats">
          <span>공고 {{ user.jobCount || 0 }}개</span>
          <span>찜 {{ user.bookmarks?.length || 0 }}개</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { collection, getDocs, updateDoc, doc, query, orderBy } from 'firebase/firestore'
import { db } from '@/services/firebase'

export default {
  name: 'AdminUsersView',
  setup() {
    const users = ref([])
    const loading = ref(true)
    const searchQuery = ref('')

    const filteredUsers = computed(() => {
      if (!searchQuery.value) return users.value
      return users.value.filter(user => 
        user.email?.toLowerCase().includes(searchQuery.value.toLowerCase())
      )
    })

    const fetchUsers = async () => {
      loading.value = true
      try {
        const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'))
        const snapshot = await getDocs(q)
        users.value = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      } catch (e) {
        console.error('유저 로드 실패:', e)
      } finally {
        loading.value = false
      }
    }

    const updateRole = async (user) => {
      try {
        await updateDoc(doc(db, 'users', user.id), {
          role: user.role
        })
        alert(`${user.email}의 역할이 변경되었습니다.`)
      } catch (e) {
        console.error('역할 변경 실패:', e)
        alert('역할 변경에 실패했습니다.')
      }
    }

    const formatDate = (date) => {
      if (!date) return '-'
      if (date.toDate) date = date.toDate()
      return new Date(date).toLocaleDateString('ko-KR')
    }

    onMounted(fetchUsers)

    return {
      users,
      loading,
      searchQuery,
      filteredUsers,
      updateRole,
      formatDate
    }
  }
}
</script>

<style scoped>
.admin-users-page {
  padding: 40px;
}

.page-header {
  margin-bottom: 32px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: #191f28;
  margin-bottom: 8px;
}

.page-subtitle {
  font-size: 15px;
  color: #6b7684;
}

.search-bar {
  margin-bottom: 24px;
}

.search-bar input {
  width: 100%;
  max-width: 400px;
  padding: 14px 16px;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  font-size: 15px;
  background: #fff;
}

.loading-state {
  text-align: center;
  padding: 60px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f2f4f6;
  border-top-color: #3182f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.user-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.user-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.user-avatar {
  width: 48px;
  height: 48px;
  background: #3182f6;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 600;
}

.user-info {
  flex: 1;
  min-width: 0;
}

.user-info h3 {
  font-size: 15px;
  font-weight: 600;
  color: #191f28;
  margin-bottom: 4px;
}

.user-info p {
  font-size: 13px;
  color: #6b7684;
}

.user-role select {
  padding: 8px 12px;
  border: 1px solid #e5e8eb;
  border-radius: 8px;
  font-size: 14px;
  background: #fff;
}

.user-stats {
  display: flex;
  gap: 16px;
  font-size: 13px;
  color: #6b7684;
}

@media (max-width: 768px) {
  .admin-users-page {
    padding: 24px 16px;
  }

  .user-item {
    flex-wrap: wrap;
  }

  .user-stats {
    width: 100%;
    margin-top: 8px;
  }
}
</style>